import React, { useMemo, useEffect } from "react";
import { Table, TableProps, TableColumn, defaultRowId } from "@components/new/Table";
import DataView, { DataViewProps } from "./DataViewNew";

export type { BeforeProps } from "./DataView";
import type { DataViewHandle as DataViewHandle_ } from "./DataView";
export type DataViewHandle<T = any> = DataViewHandle_<T>;

type TableExtraProps<T, TNestedData> = Omit<TableProps<T, TNestedData>, "data">
type DataViewExtraProps<T, BP, AP, TNestedData> = Omit<DataViewProps<T, TableProps<T, TNestedData>, BP, AP>, "View">
// type DataTableProps<T> = DataViewExtraProps<T> & TableExtraProps<T>
type DataTableProps<T, BP, AP, TNestedData> = DataViewExtraProps<T, BP, AP, TNestedData> & TableExtraProps<T, TNestedData> & {
  dataRef?: React.MutableRefObject<DataViewHandle | null>;
}

function View<T, TNestedData>(props: TableProps<T, TNestedData>) {
  return (
    <Table<T, TNestedData>
      {...props}
    />
  );
}

function areViewPropsEqual<T, TNestedData>(oldProps: TableProps<T, TNestedData>, newProps: TableProps<T, TNestedData>) {
  const rowId = oldProps.rowId || defaultRowId<T>;
  if (
    oldProps.onSelectionChange !== newProps.onSelectionChange
    || oldProps.columns !== newProps.columns
    || oldProps.nestedColumns !== newProps.nestedColumns
    || oldProps.selectable !== newProps.selectable
    || oldProps.className !== newProps.className
    // || oldProps.wrapperClassName !== newProps.wrapperClassName
    || oldProps.isHeadless !== newProps.isHeadless
    || oldProps.sorting !== newProps.sorting
    || JSON.stringify(oldProps.data.map(v => rowId(v))) !== JSON.stringify(newProps.data.map(v => rowId(v)))
  ) return false
  return JSON.stringify(oldProps.selectedIds) === JSON.stringify(newProps.selectedIds)
}

const ViewMemo = React.memo(View, areViewPropsEqual) as typeof View

function DataTable<T, BP=void, AP=void, TNestedData=T>({
  pending,
  queryKey,
  getData,
  defaultPageSize,
  notFound,
  filters,
  Before,
  After,
  beforeProps,
  afterProps,
  dataRef,
  withPagination,
  onUpdate,
  noLoaderOnRefresh,
  noDataProps,
  resetOnUpdate,
  pageSizeOptions,
  keepSearchParams,
  keepSelectionOnFiltersChange,
  hidePaginationDropdown,
  ...restProps
}: DataTableProps<T, BP, AP, TNestedData>) {
  useEffect(() => {
    if (restProps.onSelectionChange && !keepSelectionOnFiltersChange) {
      restProps.onSelectionChange([], [])
    }
  }, [JSON.stringify(filters)]);

  const skeleton = useMemo(() => (
    <Table<null>
      isSkeleton
      columns={restProps.columns as TableColumn<null>[]}
      data={[null, null, null, null]}
      selectable={restProps.selectable}
      isHeadless={restProps.isHeadless}
      verticalAlignment={restProps.verticalAlignment}
    />
  ), [restProps.columns, restProps.isHeadless])

  return (
    <DataView<T, TableExtraProps<T, TNestedData>, BP, AP>
      pending={pending}
      queryKey={queryKey}
      getData={getData}
      defaultPageSize={defaultPageSize}
      notFound={notFound}
      filters={filters}
      sorting={restProps.sorting}
      Before={Before}
      After={After}
      beforeProps={beforeProps}
      afterProps={afterProps}
      View={ViewMemo<T, TNestedData>}
      viewProps={restProps}
      dataRef={dataRef}
      withPagination={withPagination}
      onUpdate={onUpdate}
      noLoaderOnRefresh={noLoaderOnRefresh}
      noDataProps={noDataProps}
      skeleton={skeleton}
      resetOnUpdate={resetOnUpdate}
      pageSizeOptions={pageSizeOptions}
      keepSearchParams={keepSearchParams}
      hidePaginationDropdown={hidePaginationDropdown}
      // isRowCollapsible={isRowCollapsible}
      // getNestedData={getNestedData}
    />
  );
}

export default DataTable;
